/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import clsx from "clsx";
import Dates from "utils/Dates";
import {TimeslotFilled} from "./TimeslotFilled";
import {DEFAULT_HALL, FILTER_TIMESLOTS_PER_PERSON_TYPE, LOCALE, USE_POSSIBLE_TIMESLOTS_ALLOWED} from "helpers/config";
import {moduloMinutesForFakeTimeslots} from "utils/timeslotUtils";
import React from "react";
import {Timeslot} from "types/entities/Timeslot";
import {isAdult, isChild, isFamily, isUnknown} from "utils/utility";
import {useTypedSelector} from "helpers/reducers";
import {useTranslation} from "react-i18next";
import {CheckCircleIcon, ExclamationTriangleIcon, UsersIcon} from "@heroicons/react/24/solid";
import {HallCode} from "types/enums/HallCode";
import {getAvailableKartingDuration} from "utils/packageUtils";
import {PackageCategory} from "types/enums/PackageCategory";

interface BookingTimeslotProps {
    index: number;
    customTimeslotIndex: number | null;
    firstPossibleSlotIndex: number | null;
    lastPossibleSlotIndex: number | null;
    timeslot: Timeslot;
    packageRespectsTimeslotSize: boolean | null;
    selectedTimeslot: Timeslot | null;
    onClick: () => void;
    fakeTimeslotMinute: boolean;
    possibleTimeslotIndex: number;
    possibleTimeslotsLength: number;
    showTimeslotOccupancy: boolean;
    showEventHoursCategory: boolean;
}

const BookingTimeslot = (props: BookingTimeslotProps) => {
    const {t} = useTranslation();
    const filter = useTypedSelector(state => state.bookingState.bookingProcess.filter);

    const blockedClass = "blockedClass bg-gray-600 pointer-events-none opacity-50";
    const notPossibleClass = props.customTimeslotIndex !== null ? "notPossible pointer-events-none !bg-gray-800/50 opacity-50" : "!bg-gray-800/50 opacity-50";
    const personPlaceRequired = +filter.adults + +filter.children;
    const fitsIntoTimeslot = props.timeslot.persons_max - props.timeslot.persons_filled - personPlaceRequired >= 0;

    const isFitForAdult = props.timeslot.persons_filled_child === 0 && (isAdult(props.timeslot.persons_type) || isUnknown(props.timeslot.persons_type));
    const isFitForChild = props.timeslot.persons_filled_adult === 0 && (isChild(props.timeslot.persons_type) || isUnknown(props.timeslot.persons_type));
    const isFitForFamily = props.timeslot.persons_filled === 0 || isFamily(props.timeslot.persons_type) || isUnknown(props.timeslot.persons_type);

    const isFitForFilterPersons = FILTER_TIMESLOTS_PER_PERSON_TYPE
        ? (isFitForChild && filter.children > 0) || (isFitForAdult && filter.children === 0) || (isFitForFamily && filter.children > 0)
        : true;
    const isBlockedTimeslot = !props.packageRespectsTimeslotSize
        ? props.packageRespectsTimeslotSize
        : !fitsIntoTimeslot || props.timeslot.blocked_at !== null || !isFitForFilterPersons;

    const getDescriptionTextByIndex = (index: number) => {
        return DEFAULT_HALL === HallCode.ArboPark ? t("hour") + " #" + (1 + index) : t("race") + " #" + (1 + index);
    };

    // const getAvailableKartingDuration = (timeslot: Timeslot) => {
    //     if (!timeslot.karting_available_array || Object.keys(timeslot.karting_available_array).length === 0) {
    //         return 0;
    //     }
    //
    //     const available_array = Object.keys(timeslot.karting_available_array).filter(key => key != "0");
    //     const values = available_array.map(key => timeslot.karting_available_array[key]);
    //     const allTrue = values.every(value => value);
    //     const allFalse = values.every(value => !value);
    //
    //     if (allFalse) return -100;
    //     if (allTrue) return 100;
    //     return 0;
    // };

    const isAfterSixEvening = parseInt(Dates.dayjs(props.timeslot.start_at).format("H")) >= 18;
    return (
        <button
            className={clsx(
                isBlockedTimeslot && blockedClass,
                USE_POSSIBLE_TIMESLOTS_ALLOWED ? props.lastPossibleSlotIndex && props.index > props.lastPossibleSlotIndex && notPossibleClass : "",
                USE_POSSIBLE_TIMESLOTS_ALLOWED ? props.firstPossibleSlotIndex && props.index < props.firstPossibleSlotIndex && notPossibleClass : "",
                props.selectedTimeslot?.id !== props.timeslot.id && "ring-transparent",
                props.selectedTimeslot?.id === props.timeslot.id ? "border-black" : "border-gray-300",
                props.customTimeslotIndex !== -1 && "!ring-blue-400",
                props.possibleTimeslotIndex !== -1 && "outline-dashed outline-2 outline-offset-2 outline-cta-800",
                "BookingTimeslotButton border border-solid  hover:border-black bg-white rounded-lg relative cursor-pointer active:no-underline hover:no-underline text-center text-base sm:text-lg font-bold flex flex-col sm:flex-row items-center place-items-stretch"
            )}
            data-timeslot-id={props.timeslot.id}
            id={
                props.index !== 0 && props.possibleTimeslotIndex !== -1
                    ? "timeslot-possible-index-" + props.possibleTimeslotIndex
                    : "timeslot-index-" + props.index
            }
            type={"button"}
            onClick={props.onClick}
        >
            {props.showTimeslotOccupancy && <TimeslotFilled timeslot={props.timeslot} />}
            {props.timeslot.isOooh && (
                <div className={"bg-red-500/30 h-full w-full absolute top-0 left-0"}>
                    <ExclamationTriangleIcon className={"text-white/60 w-12 h-12 absolute bottom-1/2 translate-y-1/2 right-1/2 translate-x-1/2"} />
                </div>
            )}

            <div
                className={clsx(
                    "min-w-[50px] w-full flex flex-col items-center justify-center sm:w-auto border-content rounded-tl-lg rounded-tr-lg sm:rounded-tr-none sm:rounded-bl-lg py-1 sm:py-5 px-2.5 z-10 place-self-stretch",
                    props.selectedTimeslot?.id === props.timeslot.id ? "bg-black text-white" : "bg-gray-100"
                )}
            >
                <span>
                    {!props.fakeTimeslotMinute ? Dates.time(props.timeslot.start_at)?.toLocaleUpperCase(LOCALE) : moduloMinutesForFakeTimeslots(props.timeslot)}
                </span>
            </div>
            {props.showTimeslotOccupancy && props.packageRespectsTimeslotSize && (
                <div className={"BookingOccupancy text-xs z-10 flex flex-row items-center justify-between"}>
                    <div className={"inline-flex"}>
                        <span>{Number(((props.timeslot.persons_filled / props.timeslot.persons_max) * 100).toFixed(2))}%</span>
                    </div>
                    <UsersIcon className={"ml-1 w-3 h-3 object-cover"} xlinkTitle={"Occupancy"} />
                </div>
            )}
            {props.possibleTimeslotIndex !== -1 && (
                <div className={"z-2 absolute capitalize bg-cta-800 px-1 py-[1px] font-medium -bottom-2 text-contrast-100 text-xs rounded-full"}>
                    {getDescriptionTextByIndex(props.possibleTimeslotIndex)}
                </div>
            )}

            {props.customTimeslotIndex !== null && props.customTimeslotIndex !== -1 && (
                <div className={"z-3 absolute capitalize bg-blue-400 px-1 py-[1px] -bottom-2 text-white text-xs rounded-full"}>
                    {getDescriptionTextByIndex(props.customTimeslotIndex)}
                </div>
            )}
            <div className={"getAvailableKartingDuration pl-1 py-2 relative flex grow flex-col items-center justify-center text-xxs leading-4 font-light"}>
                {getAvailableKartingDuration(props.timeslot) == 100 && (
                    <div className={"w-full pl-2 pr-1 flex items-center justify-between gap-x-1 ticket-all-karting"}>
                        <span>{t("All ticket types")}</span>
                        <CheckCircleIcon className={"text-arbo-green size-4"} />
                    </div>
                )}

                {getAvailableKartingDuration(props.timeslot) == -100 && (
                    <div className={"w-full pl-2 pr-1 flex items-center justify-between gap-x-1 ticket-without-karting-only"}>
                        <span>{t("Only ticket without karting")}</span>
                        <CheckCircleIcon className={"text-arbo-yellow size-4"} />
                    </div>
                )}
                {getAvailableKartingDuration(props.timeslot) == 0 && (
                    <>
                        {(!props.timeslot.karting_available || !props.timeslot.karting_available_4h || !props.timeslot.karting_available_6h) && (
                            <div className={"w-full pl-2 pr-1 flex items-center justify-between gap-x-1 ticket-without-karting"}>
                                <span>{t("Ticket without karting")}</span>
                                <CheckCircleIcon className={"text-arbo-yellow size-4"} />
                            </div>
                        )}
                        {Object.keys(props.timeslot.karting_available_array).map((duration, index) => {
                            if (!props.timeslot.karting_available_array[duration]) {
                                return null;
                            }
                            if (props.showEventHoursCategory) {
                                if ([PackageCategory.MegaFunSchool, PackageCategory.SchoolTrophy].includes(filter.category as PackageCategory)) {
                                    if (duration !== "2.0" && duration !== "3.0" && duration !== "4.5") {
                                        return null;
                                    }
                                }
                                if (duration !== "3.0" && duration !== "4.5" && duration !== "6.0") {
                                    return null;
                                }
                            } else {
                                if (isAfterSixEvening) {
                                    if (!["1.0", "2.0", "4.0", "6.0"].includes(duration)) {
                                        return null;
                                    }
                                } else if (!["2.0", "4.0", "6.0"].includes(duration)) {
                                    return null;
                                }
                            }
                            return (
                                <div
                                    key={index}
                                    className={clsx(
                                        "w-full pl-2 pr-1 flex items-center justify-between gap-x-1",
                                        "ticket-with-karting-" + duration.toString().replace(".0", "")
                                    )}
                                >
                                    <span>{t("hour_ticket_with_karting", {hour: duration.toString().replace(".0", "")})}</span>
                                    <CheckCircleIcon className={"text-arbo-red size-4"} />
                                </div>
                            );
                        })}
                    </>
                )}
            </div>
            {/*<div className={"absolute right-1 bottom-1 flex flex-row gap-1"}>*/}
            {/*    <span className={"size-2.5 rounded-full bg-arbo-yellow"}></span>*/}
            {/*    {props.timeslot.karting_available ? (*/}
            {/*        <span className={"size-2.5 rounded-full bg-arbo-red"}></span>*/}
            {/*    ) : (*/}
            {/*        <span className={"size-2.5 rounded-full bg-gray-600"}></span>*/}
            {/*    )}*/}
            {/*</div>*/}
        </button>
    );
};

export default BookingTimeslot;
